import { BaseClient, normalizeQuery } from '../base-client';
import type {
  ApplyTo,
  EditImpactResponse,
  EntityVersionAmendResponse,
  EntityVersionsResponse,
  PatchMeta,
} from '../entity-versions/types';
import type {
  CreateCustomerInput,
  Customer,
  ExportCustomersQuery,
  ImportCustomersResult,
  ListCustomersQuery,
  ListSubsidiariesQuery,
  PatchCustomerInput,
  PauseInvoicingInput,
} from './types';

type One = { data: Customer };
type ListResponse = { data: Customer[]; meta: { total: number; limit: number; offset: number } };
type ImportResponse = { data: ImportCustomersResult };

export class CustomersClient extends BaseClient {
  list(query: ListCustomersQuery = {}): Promise<ListResponse> {
    return this.request('/api/customers', { query: normalizeQuery(query) });
  }

  /** Batch fetch customers by exactly the referenced ids — bounded, never
   *  capped. See `BaseClient.findByIdsAt`. */
  findByIds(ids: readonly string[]): Promise<ListResponse> {
    return this.findByIdsAt<Customer>('/api/customers/find', ids);
  }

  /** Like {@link findByIds} but the server drops anything not `active`. Used
   *  to source pickers that must only ever OFFER active customers (e.g. the
   *  timesheet customer dropdown) without filtering on the client. Resolving
   *  the label of an already-bound row stays on `findByIds` so an entry that
   *  points at a since-deactivated customer still renders. */
  findActiveByIds(ids: readonly string[]): Promise<ListResponse> {
    return this.findByIdsAt<Customer>('/api/customers/find', ids, [
      { field: 'status', op: 'eq', value: 'active' },
    ]);
  }

  get(id: string): Promise<One> {
    return this.request(`/api/customers/${encodeURIComponent(id)}`);
  }

  /** The customers whose parent is `id`. A nested collection in its own right,
   *  not a slice of the customer record — see `ListSubsidiariesQuery`. */
  listSubsidiaries(id: string, query: ListSubsidiariesQuery = {}): Promise<ListResponse> {
    return this.request(`/api/customers/${encodeURIComponent(id)}/subsidiaries`, {
      query: normalizeQuery(query),
    });
  }

  /** This customer's record history, newest effective date first. */
  listVersions(id: string): Promise<EntityVersionsResponse> {
    return this.request(`/api/customers/${encodeURIComponent(id)}/versions`);
  }

  /**
   * Amend what a PAST version of this record says, in place (D10). Twin of the
   * employee client's method — see there for why no date is sent and what the
   * response carries.
   */
  amendVersion(
    id: string,
    version: number,
    input: PatchCustomerInput,
  ): Promise<EntityVersionAmendResponse> {
    return this.request(`/api/customers/${encodeURIComponent(id)}/versions/${version}`, {
      method: 'PATCH',
      body: input,
    });
  }

  create(input: CreateCustomerInput): Promise<One> {
    return this.request('/api/customers', { method: 'POST', body: input });
  }

  /** `applyTo` is the answer to "When did this become true?" — omitted,
   *  the edit applies from today (see {@link ApplyTo}). */
  patch(
    id: string,
    input: PatchCustomerInput,
    applyTo?: ApplyTo,
  ): Promise<One & { meta?: PatchMeta }> {
    return this.request(`/api/customers/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: input,
      query: { applyTo },
    });
  }

  /** What this edit would change and how many logged timesheet entries the
   *  "always true" answer would restate. Writes nothing. */
  previewPatch(id: string, input: PatchCustomerInput): Promise<EditImpactResponse> {
    return this.request(`/api/customers/${encodeURIComponent(id)}/edit-preview`, {
      method: 'POST',
      body: input,
    });
  }

  remove(id: string): Promise<One> {
    return this.request(`/api/customers/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      body: {},
    });
  }

  restore(id: string): Promise<One> {
    return this.request(`/api/customers/${encodeURIComponent(id)}/restore`, {
      method: 'POST',
      body: {},
    });
  }

  pauseInvoicing(id: string, input: PauseInvoicingInput = {}): Promise<One> {
    return this.request(`/api/customers/${encodeURIComponent(id)}/pause-invoicing`, {
      method: 'POST',
      body: input,
    });
  }

  resumeInvoicing(id: string): Promise<One> {
    return this.request(`/api/customers/${encodeURIComponent(id)}/resume-invoicing`, {
      method: 'POST',
      body: {},
    });
  }

  /**
   * Download the tenant's customers as an Excel (.xlsx) blob. `filter`
   * mirrors the list-page filters (status, custom fields, search text, sort);
   * pass `{}` to export every customer. All columns (flattened address +
   * custom fields) are included so the file round-trips through `importFile`.
   */
  exportFile(filter: ExportCustomersQuery = {}): Promise<{ blob: Blob; filename: string | null }> {
    return this.requestBlob('/api/customers/export', { query: normalizeQuery(filter) });
  }

  /** Download a blank Excel (.xlsx) template with only the import header row. */
  exportTemplate(): Promise<{ blob: Blob; filename: string | null }> {
    return this.requestBlob('/api/customers/import/template', {});
  }

  /**
   * Upload an .xlsx or .csv file. Best-effort upsert keyed on Client ID:
   * valid rows persist, invalid rows come back in the result with their
   * per-row errors. Pass `idempotencyKey` to make the POST retry-safe.
   */
  importFile(file: File, opts: { idempotencyKey?: string } = {}): Promise<ImportResponse> {
    const fd = new FormData();
    fd.append('file', file, file.name);
    const headers: Record<string, string> = {};
    if (opts.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey;
    return this.requestMultipart('/api/customers/import', fd, { headers });
  }
}
